/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useLocation, NavLink } from 'react-router-dom';
import {
  BookOpen,
  Users,
  CirclePlus,
  GraduationCap,
  LayoutDashboard,
  BookMarked,
  Settings,
  Award,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '@store/auth.store';
import { useViewModeStore } from '@store/viewMode.store';
import { useSidebarStore } from '@store/sidebar.store';
import { APP_NAME, PRODUCT_TAGLINE } from '@/lib/branding';
import amidaLogo from '../../assets/amida-logo.png';
import amidaMark from '../../assets/amida-mark.png';

const adminNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/courses', icon: BookOpen, label: 'Courses' },
  { to: '/users-groups', icon: Users, label: 'Users & Groups' },
  // Course creation is chat-only for now: "Create Course" points at the Chat
  // Studio (/chat), and the manual create-course route/page is disabled.
  // { to: '/create-course', icon: CirclePlus, label: 'Create Course' },
  { to: '/chat', icon: CirclePlus, label: 'Create Course' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const learnerNav = [
  { to: '/learn/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/learn', icon: GraduationCap, label: 'My Courses' },
  { to: '/learn/certificates', icon: Award, label: 'Certificates' },
];

const helpNav = [{ to: '/doc', icon: BookMarked, label: 'Documentation' }];

function NavItem({
  to,
  icon: Icon,
  label,
  collapsed,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  collapsed: boolean;
}) {
  const location = useLocation();
  const active =
    to === '/learn'
      ? location.pathname === '/learn'
      : to === '/users-groups'
        ? location.pathname.startsWith('/users-groups') || location.pathname.startsWith('/groups/')
        : location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={clsx(
        'relative flex items-center gap-2.5 rounded-md px-2 py-2.5 text-sm transition-colors',
        collapsed && 'justify-center',
        active ? 'bg-primary-50 text-primary font-medium' : 'text-foreground hover:bg-muted'
      )}
    >
      {/*
        The active fill is primary-50 on a near-white rail: 1.11:1, which is not
        a state indicator anyone can see. A 3px bar on the leading edge makes the
        selection structural, so it survives greyscale, a dimmed screen, and the
        collapsed rail where the label is gone.
      */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-0 w-[3px] rounded-r bg-primary"
        />
      )}
      <Icon size={18} aria-hidden="true" className="shrink-0" />
      <span className={collapsed ? 'sr-only' : 'whitespace-nowrap overflow-hidden'}>{label}</span>
    </NavLink>
  );
}

function SectionLabel({ children, collapsed }: { children: React.ReactNode; collapsed: boolean }) {
  if (collapsed) {
    // Icons-only rail: a thin divider stands in for the section heading
    // visually, and the heading text moves to a screen-reader-only node so the
    // grouping survives the collapse.
    return (
      <>
        {/* border-border, not white/40: white at 40% over this near-white
            gradient composites to #ffffff, i.e. 1.00:1 against the rail. The
            separator was not faint, it was absent. */}
        <div className="mx-2 my-2.5 border-t border-border" aria-hidden="true" />
        <p className="sr-only">{children}</p>
      </>
    );
  }
  return (
    <p className="px-2 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </p>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SidebarLogo({ logoTo, collapsed }: { logoTo: string; collapsed: boolean }) {
  return (
    <NavLink
      to={logoTo}
      className={clsx(
        'flex items-center gap-3 py-4 border-b border-border hover:opacity-80 transition-opacity',
        collapsed ? 'justify-center px-0' : 'px-5'
      )}
      aria-label="Go to dashboard"
    >
      {/* Amida "A" mark (same asset as the login page). */}
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white p-1 shadow-sm ring-1 ring-primary/20">
        <img src={amidaMark} alt="Amida" className="max-h-full max-w-full object-contain" />
      </span>
      {!collapsed && (
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="break-words text-[19px] font-semibold tracking-tight text-primary">
            {APP_NAME}
          </span>
          <span className="break-words text-[12px] font-medium tracking-wide text-muted-foreground">
            {PRODUCT_TAGLINE}
          </span>
        </span>
      )}
    </NavLink>
  );
}

function SidebarFooter() {
  return (
    <div className="flex flex-col gap-2 px-5 py-5">
      <div className="flex items-center gap-2">
        <span className="text-[13px] text-muted-foreground">Powered by</span>
        <img src={amidaLogo} alt="Amida" className="h-5 w-auto object-contain" />
      </div>
      <span className="text-[13px] text-muted-foreground">© 2026 All Rights Reserved.</span>
    </div>
  );
}

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={collapsed ? label : undefined}
      className={clsx(
        'mx-3 flex items-center gap-2.5 rounded-md px-2 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted',
        collapsed && 'justify-center'
      )}
    >
      {collapsed ? (
        <PanelLeftOpen size={18} aria-hidden="true" className="shrink-0" />
      ) : (
        <PanelLeftClose size={18} aria-hidden="true" className="shrink-0" />
      )}
      {!collapsed && <span className="whitespace-nowrap overflow-hidden">Collapse</span>}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Sidebar() {
  const { user } = useAuthStore();
  const { mode: viewMode } = useViewModeStore();
  const { collapsed, toggle } = useSidebarStore();
  // Admin toggle in the header is the single source of truth.
  const showManagementNav = user?.role !== 'learner' && viewMode === 'manage';
  const navItems = showManagementNav ? adminNav : learnerNav;
  const logoTo = showManagementNav ? '/dashboard' : '/learn/dashboard';

  return (
    <aside
      className={clsx(
        'flex flex-col shrink-0 min-h-0 h-full grad-sidebar border-r border-border transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-[72px]' : 'w-[256px]'
      )}
    >
      <SidebarLogo logoTo={logoTo} collapsed={collapsed} />

      {/* Navigation */}
      <nav
        aria-label="Main"
        className="flex-1 px-3 py-3 space-y-1 overflow-y-auto overflow-x-hidden"
      >
        {navItems.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} />
        ))}
        <SectionLabel collapsed={collapsed}>Help</SectionLabel>
        {helpNav.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} />
        ))}
      </nav>

      <div className="mt-auto flex flex-col pb-2">
        <CollapseToggle collapsed={collapsed} onToggle={toggle} />
        {!collapsed && <SidebarFooter />}
      </div>
    </aside>
  );
}
